import {
  DashboardBlock,
  DashboardPortfolio,
  DashboardUnit,
  NightStatus
} from '../../shared/ui-models/occupancy-dashboard.ui-model';
import { OccupancyBlockCostClass } from '../../shared/models/occupancy-dashboard.model';

/**
 * Every figure on the occupancy dashboard, computed from the night-by-night portfolio.
 *
 * Ported from the revenue management team's prototype, with the formulas unchanged:
 * - Net occupancy = sold ÷ (total − blocked). Gross occupancy = sold ÷ total. Block share =
 *   blocked ÷ total.
 * - ADR = revenue ÷ billed sold nights. RevPAR = revenue ÷ total nights.
 * - A blocked night is valued at the rate ladder's rate for that unit and month (exposure), and
 *   expected loss is exposure × the conversion the unit type would have achieved.
 *
 * What production changed, relative to the prototype's generated data:
 * - "Total" is inventory: listed nights only. Unlisted nights are outside every denominator.
 * - A sold night with no payment yet carries no rate. ADR and the rate ladder skip those nights
 *   rather than reading them as sold at zero.
 */

export interface DashboardFilters {
  unitTypeGroup: string[];
  unitType: string[];
  area: string[];
  view: string[];
  months: string[];
  project: string[];
  building: string[];
  landlord: string[];
  currency: string[];
  nationality: string[];
  source: string[];
  reason: string[];
  owner: string[];
}

export const EMPTY_FILTERS: DashboardFilters = {
  unitTypeGroup: [],
  unitType: [],
  area: [],
  view: [],
  months: [],
  project: [],
  building: [],
  landlord: [],
  currency: [],
  nationality: [],
  source: [],
  reason: [],
  owner: []
};

export type GroupKey =
  | 'project'
  | 'area'
  | 'view'
  | 'unitType'
  | 'unitTypeGroup'
  | 'landlord'
  | 'building';

export type ConversionMode = 'fixed' | 'actual';

export interface ModelParams {
  fromIdx: number;
  toIdx: number;
  groupBy: GroupKey;
  conversionMode: ConversionMode;
  fixedRate: number;
}

export const pct = (n: number, d: number): number | null => (d > 0 ? (n / d) * 100 : null);

const isInventory = (s: number) => s !== NightStatus.UNLISTED;
const isSellable = (s: number) => s === NightStatus.FREE || s === NightStatus.SOLD;

export const unitMatches =
  (f: DashboardFilters) =>
  (u: DashboardUnit): boolean =>
    (!f.unitTypeGroup.length || f.unitTypeGroup.includes(u.unitTypeGroup)) &&
    (!f.unitType.length || f.unitType.includes(u.unitType)) &&
    (!f.area.length || f.area.includes(u.area)) &&
    (!f.view.length || f.view.includes(u.view)) &&
    (!f.project.length || f.project.includes(u.project)) &&
    (!f.building.length || f.building.includes(u.building)) &&
    (!f.landlord.length || f.landlord.includes(u.landlord)) &&
    (!f.currency.length || f.currency.includes(u.currency));

// ---------------------------------------------------------------------------------------------
// Rate ladder
// ---------------------------------------------------------------------------------------------

interface RevCount {
  rev: number;
  n: number;
}
interface SoldSellable {
  sold: number;
  sellable: number;
}

export interface RateLadder {
  rateFor(u: DashboardUnit, mi: number): number;
  displacementFor(u: DashboardUnit, mi: number): number;
}

/**
 * Rate ladder. A blocked night is priced at the unit's own average for that month, then its unit
 * type's, then the portfolio's; months with no realised rate yet (beyond the booking horizon) fall
 * back to the same calendar month in another year, then to all-time averages. Without the
 * fallback, a block far in the future would be costed at zero.
 *
 * Displacement is the share of sellable nights a unit type actually sold in that month, closed
 * months only, with the same calendar-month fallback.
 */
export function buildRateLadder(portfolio: DashboardPortfolio): RateLadder {
  const { units, calendar } = portfolio;
  const { months, monthOfDay, monthFirstDay, monthLength, today, totalDays } = calendar;
  const calOf = (d: number) => months[monthOfDay[d]].m;

  const unitMonth = new Map<string, RevCount>();
  const typeMonth = new Map<string, RevCount>();
  const portMonth: RevCount[] = months.map(() => ({ rev: 0, n: 0 }));
  const typeCal = new Map<string, RevCount>();
  const portCal: RevCount[] = Array.from({ length: 12 }, () => ({ rev: 0, n: 0 }));
  const unitAll = new Map<string, RevCount>();
  const portAll: RevCount = { rev: 0, n: 0 };
  const typeOcc = new Map<string, SoldSellable>();
  const typeOccCal = new Map<string, SoldSellable>();
  const portOccCal: SoldSellable[] = Array.from({ length: 12 }, () => ({ sold: 0, sellable: 0 }));

  const add = (map: Map<string, RevCount>, key: string, r: number) => {
    const a = map.get(key) || { rev: 0, n: 0 };
    a.rev += r;
    a.n += 1;
    map.set(key, a);
  };
  const addOcc = (map: Map<string, SoldSellable>, key: string, sold: boolean) => {
    const o = map.get(key) || { sold: 0, sellable: 0 };
    o.sellable += 1;
    if (sold) o.sold += 1;
    map.set(key, o);
  };

  units.forEach((u) => {
    for (let d = 0; d < totalDays; d++) {
      const s = u.status[d];
      const mi = monthOfDay[d];
      const cal = calOf(d);
      const tk = `${u.unitType}|${mi}`;
      const tck = `${u.unitType}|${cal}`;

      if (isSellable(s) && d < today) {
        const sold = s === NightStatus.SOLD;
        addOcc(typeOcc, tk, sold);
        addOcc(typeOccCal, tck, sold);
        portOccCal[cal].sellable += 1;
        if (sold) portOccCal[cal].sold += 1;
      }

      const r = u.rateOfDay[d];
      if (s !== NightStatus.SOLD || !(r > 0)) continue;
      add(unitMonth, `${u.id}|${mi}`, r);
      add(typeMonth, tk, r);
      add(typeCal, tck, r);
      add(unitAll, u.id, r);
      portMonth[mi].rev += r;
      portMonth[mi].n += 1;
      portCal[cal].rev += r;
      portCal[cal].n += 1;
      portAll.rev += r;
      portAll.n += 1;
    }
  });

  const avg = (o?: RevCount): number | null => (o && o.n ? o.rev / o.n : null);
  const share = (o?: SoldSellable): number | null => (o && o.sellable ? o.sold / o.sellable : null);
  const first = (...values: (number | null)[]): number => {
    for (const v of values) if (v !== null) return v;
    return 0;
  };

  return {
    rateFor(u, mi) {
      const cal = months[mi].m;
      return first(
        avg(unitMonth.get(`${u.id}|${mi}`)),
        avg(typeMonth.get(`${u.unitType}|${mi}`)),
        avg(portMonth[mi]),
        avg(typeCal.get(`${u.unitType}|${cal}`)),
        avg(portCal[cal]),
        avg(unitAll.get(u.id)),
        avg(portAll)
      );
    },
    displacementFor(u, mi) {
      const cal = months[mi].m;
      const closed = monthFirstDay[mi] + monthLength[mi] - 1 < today;
      return first(
        closed ? share(typeOcc.get(`${u.unitType}|${mi}`)) : null,
        share(typeOccCal.get(`${u.unitType}|${cal}`)),
        share(portOccCal[cal])
      );
    }
  };
}

// ---------------------------------------------------------------------------------------------
// Occupancy, blocks and block cost
// ---------------------------------------------------------------------------------------------

export interface CurrentBlock extends DashboardBlock {
  endsOn: number;
  runFor: number;
  leftToRun: number;
}

export interface UnitRow extends DashboardUnit {
  total: number;
  sold: number;
  blocked: number;
  available: number;
  revenue: number;
  ratedSold: number;
  blockCount: number;
  exposure: number;
  expectedLoss: number;
  currentBlock: CurrentBlock | null;
  longestBlock: number;
  avgBlock: number;
  net: number | null;
  gross: number | null;
  blockPct: number | null;
  adr: number | null;
  revpar: number | null;
  lossPerNight: number | null;
  [key: string]: any;
}

export interface GroupRow {
  key: string;
  units: number;
  total: number;
  sold: number;
  blocked: number;
  revenue: number;
  ratedSold: number;
  exposure: number;
  expectedLoss: number;
  available: number;
  net: number | null;
  gross: number | null;
  blockPct: number | null;
  adr: number | null;
  revpar: number | null;
}

export interface SeriesPoint {
  /** Unique x-axis category ('Sep 2026'). Short labels repeat across a window over a year. */
  key: string;
  label: string;
  long: string;
  net: number | null;
  gross: number | null;
  blockPct: number | null;
  sold: number;
  blocked: number;
  total: number;
  exposure: number;
  expectedLoss: number;
}

export interface CostRecord extends DashboardBlock {
  unitId: string;
  unitName: string;
  project: string;
  area: string;
  unitType: string;
  nightsInRange: number;
  exposure: number;
  expectedLoss: number;
  startedBefore: boolean;
  endsAfter: boolean;
}

export interface CostBucket {
  key: string;
  costClass: OccupancyBlockCostClass;
  nights: number;
  blocks: number;
  exposure: number;
  expectedLoss: number;
  units: number;
  avgLength: number;
  longest: number;
  shareNights: number | null;
  shareLoss: number | null;
}

export interface ForwardTotals {
  nights: number;
  exposure: number;
  expectedLoss: number;
}

export interface ForwardMonth {
  y: number;
  m: number;
  label: string;
  long: string;
  blocked: number;
  exposure: number;
  expectedLoss: number;
}

export interface DashboardTotals {
  units: number;
  unitsWithBlocks: number;
  unitsBlockedNow: number;
  total: number;
  sold: number;
  blocked: number;
  revenue: number;
  ratedSold: number;
  exposure: number;
  expectedLoss: number;
  available: number;
  net: number | null;
  gross: number | null;
  blockPct: number | null;
  adr: number | null;
  revpar: number | null;
  blockCount: number;
  longestBlock: number;
}

export interface DashboardModel {
  rows: UnitRow[];
  groups: GroupRow[];
  series: SeriesPoint[];
  costRecords: CostRecord[];
  byReason: CostBucket[];
  byOwner: CostBucket[];
  byProject: CostBucket[];
  byClass: CostBucket[];
  stayLevelFilter: boolean;
  stayNightsExcluded: number;
  forward: {
    yearEnd: ForwardTotals;
    all: ForwardTotals;
    lastBlockDay: number;
    byMonth: ForwardMonth[];
    currentMonth: number;
    currentMonthBlocked: number;
  };
  totals: DashboardTotals;
}

export function computeModel(
  portfolio: DashboardPortfolio,
  rates: RateLadder,
  f: DashboardFilters,
  params: ModelParams
): DashboardModel {
  const { calendar, units, sources, nationalities } = portfolio;
  const { months, monthOfDay, today, yearEnd, totalDays } = calendar;
  const { fromIdx, toIdx, groupBy, conversionMode, fixedRate } = params;

  const monthSet = f.months.length ? new Set(f.months.map(Number)) : null;
  const srcSet = f.source.length ? new Set(f.source.map((s) => sources.indexOf(s))) : null;
  const natSet = f.nationality.length
    ? new Set(f.nationality.map((s) => nationalities.indexOf(s)))
    : null;
  const reasonSet = f.reason.length ? new Set(f.reason) : null;
  const ownerSet = f.owner.length ? new Set(f.owner) : null;
  const stayLevelFilter = Boolean(srcSet || natSet);
  const matches = unitMatches(f);
  const blockMatches = (b: DashboardBlock) =>
    (!reasonSet || reasonSet.has(b.reason)) && (!ownerSet || ownerSet.has(b.owner));
  const conversion =
    conversionMode === 'fixed'
      ? () => fixedRate / 100
      : (u: DashboardUnit, mi: number) => rates.displacementFor(u, mi);

  const rows: UnitRow[] = [];
  const monthly = months.map((m) => ({
    ...m,
    total: 0,
    sold: 0,
    blocked: 0,
    exposure: 0,
    expectedLoss: 0
  }));
  const costRecords: CostRecord[] = [];
  const forwardByMonth: ForwardMonth[] = months.map((m) => ({
    ...m,
    blocked: 0,
    exposure: 0,
    expectedLoss: 0
  }));
  let stayNightsExcluded = 0;
  const fwdYearEnd: ForwardTotals = { nights: 0, exposure: 0, expectedLoss: 0 };
  const fwdAll: ForwardTotals = { nights: 0, exposure: 0, expectedLoss: 0 };
  let lastBlockDay = -1;
  let currentMonthBlocked = 0;
  const currentMonth = today >= 0 && today < totalDays ? monthOfDay[today] : -1;

  units.forEach((u) => {
    if (!matches(u)) return;
    let total = 0;
    let sold = 0;
    let revenue = 0;
    let ratedSold = 0;
    let excluded = 0;

    for (let d = fromIdx; d <= toIdx; d++) {
      const mi = monthOfDay[d];
      if (monthSet && !monthSet.has(mi)) continue;
      const s = u.status[d];
      if (!isInventory(s)) continue;
      total += 1;
      if (s !== NightStatus.SOLD) continue;
      const okSrc = !srcSet || srcSet.has(u.srcOfDay[d]);
      const okNat = !natSet || natSet.has(u.natOfDay[d]);
      if (okSrc && okNat) {
        sold += 1;
        if (u.rateOfDay[d] > 0) {
          revenue += u.rateOfDay[d];
          ratedSold += 1;
        }
      } else excluded += 1;
    }
    stayNightsExcluded += excluded;

    // The monthly series spans the whole window, not just the selected range: the chart shades
    // the range over it, so every month needs its own figure.
    for (let d = 0; d < totalDays; d++) {
      const mi = monthOfDay[d];
      if (monthSet && !monthSet.has(mi)) continue;
      const s = u.status[d];
      if (!isInventory(s)) continue;
      monthly[mi].total += 1;
      if (
        s === NightStatus.SOLD &&
        (!srcSet || srcSet.has(u.srcOfDay[d])) &&
        (!natSet || natSet.has(u.natOfDay[d]))
      ) {
        monthly[mi].sold += 1;
      }
    }

    const segs: number[] = [];
    let blocked = 0;
    let exposure = 0;
    let expectedLoss = 0;
    let blockCount = 0;
    let currentBlock: CurrentBlock | null = null;

    u.blocks.forEach((b, bi) => {
      const bEnd = b.start + b.nights - 1;
      const owns = (d: number) => d >= 0 && d < totalDays && u.blockOfDay[d] === bi;
      if (bEnd > lastBlockDay) lastBlockDay = bEnd;
      if (b.start <= today && bEnd >= today && owns(today)) {
        currentBlock = {
          ...b,
          endsOn: bEnd,
          runFor: today - b.start + 1,
          leftToRun: bEnd - today + 1
        };
      }
      if (!blockMatches(b)) return;

      for (let d = Math.max(b.start, today, 0); d <= bEnd && d < totalDays; d++) {
        if (!owns(d)) continue;
        const mi = monthOfDay[d];
        const rate = rates.rateFor(u, mi);
        const loss = rate * conversion(u, mi);
        fwdAll.nights += 1;
        fwdAll.exposure += rate;
        fwdAll.expectedLoss += loss;
        forwardByMonth[mi].blocked += 1;
        forwardByMonth[mi].exposure += rate;
        forwardByMonth[mi].expectedLoss += loss;
        if (d <= yearEnd) {
          fwdYearEnd.nights += 1;
          fwdYearEnd.exposure += rate;
          fwdYearEnd.expectedLoss += loss;
        }
      }
      for (let d = Math.max(b.start, 0); d <= bEnd && d < totalDays; d++) {
        if (!owns(d)) continue;
        const mi = monthOfDay[d];
        if (mi === currentMonth) currentMonthBlocked += 1;
        if (monthSet && !monthSet.has(mi)) continue;
        const rate = rates.rateFor(u, mi);
        monthly[mi].blocked += 1;
        monthly[mi].exposure += rate;
        monthly[mi].expectedLoss += rate * conversion(u, mi);
      }

      const a = Math.max(b.start, fromIdx);
      const z = Math.min(bEnd, toIdx);
      if (z < a) return;
      let n = 0;
      let exp = 0;
      let loss = 0;
      for (let d = a; d <= z; d++) {
        if (!owns(d)) continue;
        const mi = monthOfDay[d];
        if (monthSet && !monthSet.has(mi)) continue;
        const rate = rates.rateFor(u, mi);
        const l = rate * conversion(u, mi);
        n += 1;
        exp += rate;
        loss += l;
      }
      if (!n) return;
      blocked += n;
      exposure += exp;
      expectedLoss += loss;
      blockCount += 1;
      segs.push(n);
      costRecords.push({
        ...b,
        unitId: u.id,
        unitName: u.name,
        project: u.project,
        area: u.area,
        unitType: u.unitType,
        nightsInRange: n,
        exposure: exp,
        expectedLoss: loss,
        startedBefore: b.start < fromIdx,
        endsAfter: bEnd > toIdx
      });
    });

    const available = total - blocked;
    rows.push({
      ...u,
      total,
      sold,
      blocked,
      available,
      revenue,
      ratedSold,
      blockCount,
      exposure,
      expectedLoss,
      currentBlock,
      longestBlock: segs.length ? Math.max(...segs) : 0,
      avgBlock: segs.length ? segs.reduce((x, y) => x + y, 0) / segs.length : 0,
      net: pct(sold, available),
      gross: pct(sold, total),
      blockPct: pct(blocked, total),
      adr: ratedSold ? revenue / ratedSold : null,
      revpar: total ? revenue / total : null,
      lossPerNight: blocked ? expectedLoss / blocked : null
    });
  });

  const sum = (k: keyof UnitRow) => rows.reduce((a, r) => a + (r[k] as number), 0);
  const total = sum('total');
  const sold = sum('sold');
  const blocked = sum('blocked');
  const revenue = sum('revenue');
  const ratedSold = sum('ratedSold');
  const exposure = sum('exposure');
  const expectedLoss = sum('expectedLoss');

  const buckets = new Map<string, GroupRow>();
  rows.forEach((r) => {
    const key = (r[groupBy] as string) || '—';
    const b =
      buckets.get(key) ||
      ({
        key,
        units: 0,
        total: 0,
        sold: 0,
        blocked: 0,
        revenue: 0,
        ratedSold: 0,
        exposure: 0,
        expectedLoss: 0
      } as GroupRow);
    b.units += 1;
    b.total += r.total;
    b.sold += r.sold;
    b.blocked += r.blocked;
    b.revenue += r.revenue;
    b.ratedSold += r.ratedSold;
    b.exposure += r.exposure;
    b.expectedLoss += r.expectedLoss;
    buckets.set(key, b);
  });
  const groups: GroupRow[] = Array.from(buckets.values())
    .map((b) => ({
      ...b,
      available: b.total - b.blocked,
      net: pct(b.sold, b.total - b.blocked),
      gross: pct(b.sold, b.total),
      blockPct: pct(b.blocked, b.total),
      adr: b.ratedSold ? b.revenue / b.ratedSold : null,
      revpar: b.total ? b.revenue / b.total : null
    }))
    .sort((a, b) => (b.net !== null ? b.net : -1) - (a.net !== null ? a.net : -1));

  const series: SeriesPoint[] = monthly.map((m) => ({
    key: m.long,
    label: m.label,
    long: m.long,
    net: pct(m.sold, m.total - m.blocked),
    gross: pct(m.sold, m.total),
    blockPct: pct(m.blocked, m.total),
    sold: m.sold,
    blocked: m.blocked,
    total: m.total,
    exposure: m.exposure,
    expectedLoss: m.expectedLoss
  }));

  const agg = (keyFn: (r: CostRecord) => string): CostBucket[] => {
    const m = new Map<
      string,
      {
        key: string;
        nights: number;
        blocks: number;
        exposure: number;
        expectedLoss: number;
        costClass: OccupancyBlockCostClass;
        unitSet: Set<string>;
        lengths: number[];
      }
    >();
    costRecords.forEach((r) => {
      const key = keyFn(r);
      const b = m.get(key) || {
        key,
        nights: 0,
        blocks: 0,
        exposure: 0,
        expectedLoss: 0,
        costClass: r.costClass,
        unitSet: new Set<string>(),
        lengths: []
      };
      b.nights += r.nightsInRange;
      b.blocks += 1;
      b.exposure += r.exposure;
      b.expectedLoss += r.expectedLoss;
      b.unitSet.add(r.unitId);
      b.lengths.push(r.nightsInRange);
      m.set(key, b);
    });
    return Array.from(m.values())
      .map((b) => ({
        key: b.key,
        costClass: b.costClass,
        nights: b.nights,
        blocks: b.blocks,
        exposure: b.exposure,
        expectedLoss: b.expectedLoss,
        units: b.unitSet.size,
        avgLength: b.lengths.reduce((a, c) => a + c, 0) / b.lengths.length,
        longest: Math.max(...b.lengths),
        shareNights: pct(b.nights, blocked),
        shareLoss: pct(b.expectedLoss, expectedLoss)
      }))
      .sort((a, b) => b.expectedLoss - a.expectedLoss);
  };

  return {
    rows,
    groups,
    series,
    costRecords,
    stayLevelFilter,
    stayNightsExcluded,
    byReason: agg((r) => r.reason),
    byOwner: agg((r) => r.owner),
    byProject: agg((r) => r.project),
    byClass: agg((r) => r.costClass),
    forward: {
      lastBlockDay,
      currentMonth,
      currentMonthBlocked,
      yearEnd: fwdYearEnd,
      all: fwdAll,
      byMonth: forwardByMonth.filter((_, i) => i >= currentMonth)
    },
    totals: {
      total,
      sold,
      blocked,
      revenue,
      ratedSold,
      exposure,
      expectedLoss,
      units: rows.length,
      unitsWithBlocks: rows.filter((r) => r.blocked > 0).length,
      unitsBlockedNow: rows.filter((r) => r.currentBlock).length,
      available: total - blocked,
      net: pct(sold, total - blocked),
      gross: pct(sold, total),
      blockPct: pct(blocked, total),
      adr: ratedSold ? revenue / ratedSold : null,
      revpar: total ? revenue / total : null,
      blockCount: rows.reduce((a, r) => a + r.blockCount, 0),
      longestBlock: rows.reduce((a, r) => Math.max(a, r.longestBlock), 0)
    }
  };
}

// ---------------------------------------------------------------------------------------------
// Booking pace
// ---------------------------------------------------------------------------------------------

export const PACE_LEADS = [120, 90, 60, 45, 30, 21, 14, 7, 3, 0];

export interface MonthStat {
  mi: number;
  label: string;
  long: string;
  first: number;
  last: number;
  sellable: number;
  final: number;
  onBooks: Record<number, number>;
  soldToday: number;
  isPast: boolean;
  isCurrent: boolean;
  daysOut: number;
  finalOcc: number | null;
  todayOcc: number | null;
}

export interface PacePoint {
  lead: number;
  label: string;
  tip: string;
  occ: number | null;
  nights: number;
  shareOfFinal: number | null;
  typicalShare: number | null;
  typicalOcc: number | null;
  reachedYet: boolean;
}

export interface ForwardBook extends MonthStat {
  expectedShare: number | null;
  impliedFinalOcc: number | null;
}

export interface DashboardPace {
  monthStats: MonthStat[];
  curve: PacePoint[];
  typical: { lead: number; shareOfFinal: number | null }[];
  target: MonthStat;
  forward: ForwardBook[];
  LEADS: number[];
}

/**
 * Pace is reconstructed from each reservation's creation date. A canceled reservation sold no
 * nights, so it never counts toward a month's final occupancy, but it was on the books between its
 * creation and its cancellation, and counts at every lead point inside that span. Cancellations
 * with no recorded cancellation date are not in the dataset at all, which still flatters historic
 * pace a little.
 */
export function computePace(
  portfolio: DashboardPortfolio,
  f: DashboardFilters,
  paceMonth: number
): DashboardPace {
  const { calendar } = portfolio;
  const { months, monthFirstDay, monthLength, today } = calendar;
  const scope = portfolio.units.filter(unitMatches(f));
  const LEADS = PACE_LEADS;

  const monthStats: MonthStat[] = months.map((mm, mi) => {
    const first = monthFirstDay[mi];
    const last = first + monthLength[mi] - 1;
    let sellable = 0;
    scope.forEach((u) => {
      for (let d = first; d <= last; d++) if (isSellable(u.status[d])) sellable += 1;
    });

    const onBooks: Record<number, number> = {};
    LEADS.forEach((l) => (onBooks[l] = 0));
    let final = 0;

    scope.forEach((u) => {
      u.bookings.forEach((b) => {
        const a = Math.max(b.start, first);
        const z = Math.min(b.start + b.nights - 1, last);
        if (z < a) return;
        let n = 0;
        for (let d = a; d <= z; d++) {
          const s = u.status[d];
          if (b.canceledAt === null ? s === NightStatus.SOLD : isInventory(s)) n += 1;
        }
        if (!n) return;
        if (b.canceledAt === null) final += n;
        LEADS.forEach((l) => {
          const asOf = first - l;
          const onTheBooks = b.bookedAt <= asOf && (b.canceledAt === null || b.canceledAt > asOf);
          if (onTheBooks) onBooks[l] += n;
        });
      });
    });

    return {
      mi,
      first,
      last,
      sellable,
      final,
      onBooks,
      label: mm.label,
      long: mm.long,
      // Every live booking in the dataset was made on or before the report date.
      soldToday: final,
      isPast: last < today,
      isCurrent: first <= today && last >= today,
      daysOut: first - today,
      finalOcc: pct(final, sellable),
      todayOcc: pct(final, sellable)
    };
  });

  const closed = monthStats.filter((m) => m.isPast && m.final > 0);
  const typical = LEADS.map((l) => {
    const vals = closed
      .map((m) => (m.final ? (m.onBooks[l] / m.final) * 100 : null))
      .filter((v): v is number => v !== null);
    return {
      lead: l,
      shareOfFinal: vals.length ? vals.reduce((a, b) => a + b, 0) / vals.length : null
    };
  });

  const target = monthStats[Math.max(0, Math.min(monthStats.length - 1, paceMonth))];
  const curve: PacePoint[] = LEADS.map((l, i) => ({
    lead: l,
    label: l === 0 ? 'Start' : `${l}d`,
    tip: l === 0 ? 'At month start' : `${l} days before`,
    occ: pct(target.onBooks[l], target.sellable),
    nights: target.onBooks[l],
    shareOfFinal: target.final ? (target.onBooks[l] / target.final) * 100 : null,
    typicalShare: typical[i].shareOfFinal,
    typicalOcc:
      typical[i].shareOfFinal !== null && target.finalOcc !== null
        ? ((typical[i].shareOfFinal as number) / 100) * target.finalOcc
        : null,
    reachedYet: target.first - l <= today
  }));

  const forward: ForwardBook[] = monthStats
    .filter((m) => !m.isPast)
    .map((m) => {
      const d = Math.max(0, m.daysOut);
      let ref = typical[typical.length - 1];
      typical.forEach((t2) => {
        if (t2.lead >= d) ref = t2;
      });
      const expectedShare = ref.shareOfFinal;
      const impliedFinal =
        expectedShare && expectedShare > 0 ? m.soldToday / (expectedShare / 100) : null;
      return {
        ...m,
        expectedShare,
        daysOut: d,
        impliedFinalOcc: impliedFinal !== null ? pct(impliedFinal, m.sellable) : null
      };
    });

  return { monthStats, curve, typical, target, forward, LEADS };
}
